import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Sparkles, ArrowLeft, Lock, CheckCircle, Play, 
  ChevronRight, Trophy, Headphones, BookOpen, PenTool, Mic,
  Unlock, CreditCard, Gift, Clock
} from 'lucide-react';

const ModuleExercises = () => {
  const { level, module } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModuleData();
  }, [level, module]);

  const fetchModuleData = async () => {
    try {
      const response = await api.get(`/modules/${level}/${module}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching module:', error);
      toast.error('Failed to load module');
      navigate(`/level/${level}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStartExercise = (exercise) => {
    if (exercise.has_content === false) {
      toast.info('This exercise is coming soon!');
      return;
    }
    
    if (!data?.is_authenticated) {
      // Redirect to login
      const redirectUrl = window.location.origin + `/exercise/${exercise.exercise_id}`;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    
    if (exercise.is_unlocked) {
      navigate(`/exercise/${exercise.exercise_id}`);
    } else if (exercise.requires_purchase) {
      toast.info('This exercise requires full access. Upgrade to unlock!');
      navigate('/pricing');
    }
  };

  const moduleConfig = {
    comprehension_orale: { 
      icon: Headphones, 
      color: "bg-blue-500",
      gradient: "from-blue-400 to-blue-600"
    },
    comprehension_ecrite: { 
      icon: BookOpen, 
      color: "bg-green-500",
      gradient: "from-green-400 to-green-600"
    },
    production_ecrite: { 
      icon: PenTool, 
      color: "bg-purple-500",
      gradient: "from-purple-400 to-purple-600"
    },
    production_orale: { 
      icon: Mic, 
      color: "bg-orange-500",
      gradient: "from-orange-400 to-orange-600"
    }
  };

  const config = moduleConfig[module] || moduleConfig.comprehension_orale;
  const Icon = config.icon;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  const completedCount = data?.progress?.completed_exercises?.length || 0;
  const totalExercises = data?.exercises?.length || 10;
  const progressPercent = (completedCount / totalExercises) * 100;

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFCF8]/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/level/${level}`} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
                DELF Success Academy
              </span>
            </Link>
          </div>
          <Button variant="ghost" onClick={() => navigate(`/level/${level}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Module Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`bg-gradient-to-r ${config.gradient} rounded-3xl p-8 mb-8 text-white`}
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                <Icon className="w-8 h-8" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-sm opacity-80 mb-1">DELF {level}</div>
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {data?.module_name_fr}
                </h1>
                <p className="text-white/80">{data?.module_name}</p>
              </div>
            </div>
            
            <div className="text-center md:text-right">
              <div className="text-4xl font-bold">{completedCount}/{totalExercises}</div>
              <p className="text-white/80">exercises completed</p>
              <Progress value={progressPercent} className="w-48 h-3 bg-white/30 mt-2" />
            </div>
          </div>
        </motion.div>

        {/* Free Trial Banner */}
        {!data?.has_full_access && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-6 border border-green-200 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Gift className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-800">Free Trial: Exercise 1 is free!</p>
                <p className="text-sm text-green-600">Experience the quality before purchasing</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/pricing')}
              className="rounded-full bg-green-600 hover:bg-green-700"
            >
              <Unlock className="w-4 h-4 mr-2" />
              Unlock Full Access
            </Button>
          </motion.div>
        )}

        {/* Mastery Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-muted/50 rounded-xl p-4 mb-6 text-center"
        >
          <p className="text-sm">
            <strong>Mastery Rule:</strong> Score at least <span className="text-primary font-bold">80%</span> to unlock the next exercise.
            {data?.progress?.exam_mode_available && (
              <span className="ml-2 text-green-600 font-medium">✓ Exam Mode Available</span>
            )}
          </p>
        </motion.div>

        {/* Exercises Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data?.exercises?.map((exercise, index) => {
            const isFreeTrial = exercise.is_free_trial;
            const requiresPurchase = exercise.requires_purchase && !isFreeTrial;
            const hasContent = exercise.has_content !== false;
            
            return (
              <motion.div
                key={exercise.exercise_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
                className={`bg-white rounded-2xl border overflow-hidden transition-all duration-300 ${
                  !hasContent
                    ? 'border-border/30 opacity-50'
                    : exercise.is_unlocked 
                    ? 'border-border/50 hover:shadow-lg hover:border-primary/30 cursor-pointer' 
                    : requiresPurchase 
                    ? 'border-orange-200 bg-orange-50/30' 
                    : 'border-border/50 opacity-60'
                }`}
                onClick={() => handleStartExercise(exercise)}
                data-testid={`exercise-${exercise.exercise_number}`}
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      !hasContent ? 'bg-muted' :
                      exercise.is_completed ? 'bg-green-100' : 
                      exercise.is_unlocked ? config.color + '/10' : 
                      isFreeTrial ? 'bg-green-100' :
                      requiresPurchase ? 'bg-orange-100' : 'bg-muted'
                    }`}>
                      {!hasContent ? (
                        <Clock className="w-6 h-6 text-muted-foreground" />
                      ) : exercise.is_completed ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : exercise.is_unlocked ? (
                        <Play className="w-6 h-6 text-primary" />
                      ) : isFreeTrial ? (
                        <Gift className="w-6 h-6 text-green-600" />
                      ) : requiresPurchase ? (
                        <CreditCard className="w-6 h-6 text-orange-600" />
                      ) : (
                        <Lock className="w-6 h-6 text-muted-foreground" />
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {!hasContent && (
                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-muted text-muted-foreground">
                          SOON
                        </span>
                      )}
                      {isFreeTrial && !exercise.is_completed && hasContent && (
                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-green-100 text-green-700">
                          FREE
                        </span>
                      )}
                      {exercise.best_score !== null && (
                        <span className={`text-sm font-bold px-2 py-1 rounded-full ${
                          exercise.best_score >= 20 ? 'bg-green-100 text-green-700' : 'bg-accent/10 text-accent'
                        }`}>
                          {exercise.best_score}/25
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <h3 className="font-bold mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
                    Exercise {exercise.exercise_number}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">{exercise.title_fr}</p>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{exercise.time_limit_minutes} min</span>
                    <span>{exercise.total_points} pts</span>
                  </div>
                </div>
                
                {/* CTA Footer */}
                <div className={`px-5 py-3 border-t flex items-center justify-between ${
                  !hasContent ? 'bg-muted/30 border-border/30' :
                  exercise.is_completed ? 'bg-green-50 border-green-100' : 
                  exercise.is_unlocked ? 'bg-primary/5 border-border/50' :
                  isFreeTrial ? 'bg-green-50 border-green-100' :
                  requiresPurchase ? 'bg-orange-50 border-orange-100' :
                  'bg-muted/50 border-border/50'
                }`}>
                  <span className={`text-sm font-medium ${
                    !hasContent ? 'text-muted-foreground' :
                    requiresPurchase ? 'text-orange-700' : ''
                  }`}>
                    {!hasContent ? 'Coming Soon' :
                     exercise.is_completed ? 'Retry' : 
                     exercise.is_unlocked ? 'Start' :
                     isFreeTrial ? 'Try Free' :
                     requiresPurchase ? 'Unlock' : 'Locked'}
                  </span>
                  {requiresPurchase ? (
                    <Lock className="w-4 h-4 text-orange-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Unlock CTA for non-subscribers */}
        {!data?.has_full_access && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8 bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8 border border-primary/20 text-center"
          >
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              Unlock Full Access
            </h3>
            <p className="text-muted-foreground mb-4">
              Get access to all 10 exercises in this module and unlock your full DELF {level} preparation
            </p>
            <Button
              onClick={() => navigate('/pricing')}
              size="lg"
              className="rounded-full px-8 font-bold"
            >
              <Unlock className="w-4 h-4 mr-2" />
              View Pricing Plans
            </Button>
          </motion.div>
        )}

        {/* Achievement Card */}
        {completedCount === totalExercises && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-8 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-8 text-white text-center"
          >
            <Trophy className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              Module Complete!
            </h2>
            <p className="text-white/80 mb-4">
              Congratulations! You've completed all exercises in this module.
            </p>
            <Button
              variant="secondary"
              className="rounded-full"
              onClick={() => navigate(`/level/${level}`)}
            >
              Back to Dashboard
            </Button>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default ModuleExercises;
